/**
 * Shareable links: what one is, how to make one, how to read one back.
 *
 * ── THE PATTERN, for the screens still to be wired ──────────────────────────
 *
 * Every screen that has something worth sharing does exactly four things, and
 * nothing else. The departures board is the worked example; copy it.
 *
 *   1. Describe what is on screen as a DESCRIPTOR — a plain object, no refs,
 *      no components, no store. `{ city, page, ...fields }`. The fields are
 *      declared in `PAGES` below and nowhere else; a screen never invents one.
 *
 *   2. Hand that descriptor to `<ShareButton :share="descriptor" />` in the
 *      masthead, beside the settings cog. The button does the rest: the link,
 *      the sentence, the native sheet, the clipboard fallback. A screen with
 *      nothing to share passes `null` and the button does not render.
 *
 *   3. Restore is NOT the screen's job either — `src/share-open.js` settles the
 *      city and lands on the right route, using the same request modules a tap
 *      already uses (`board-request.js`, `options-request.js`). A screen only
 *      has to CONSUME its request reactively, at mount, at view-enter, and on
 *      change, because a restore can arrive before, during or after the page
 *      exists. `DeparturesPage.consumeRequest` is the shape of it.
 *
 *   4. When the exact thing has gone, the screen SAYS SO. `shareNote` in
 *      share-open.js carries one dry sentence and the screen renders it under
 *      the masthead. Degrading to something near is honest; degrading to an
 *      error, or silently to something else, is not.
 *
 * ── PRIVACY. NOT NEGOTIABLE. ────────────────────────────────────────────────
 *
 * HOME AND WORK NEVER LEAVE THE DEVICE, by label or by implication. They are
 * set on this phone, stored on this phone, and a link that said "home" would
 * tell a recipient — and anyone the message is forwarded to — where the sender
 * lives. So:
 *
 *   * no descriptor field ever holds the strings 'home' or 'work', or any
 *     other place kind. `PLACE_KINDS` from options-request.js is a screen
 *     concept and stops at the screen.
 *   * a link built FROM a place carries the EXPLICIT STATION that place
 *     resolves to, which is a public stop id like any other. The recipient
 *     sees "Carnegie", not "home", and nothing in the link says which of the
 *     two ends the sender calls what.
 *   * nothing about the sender's position goes in a link. The nearby chip, the
 *     location fix, the walk radius: none of them is a field here.
 *   * the only things that ever travel are PUBLIC NETWORK FACTS — a city, a
 *     stop id, a route id, a run reference, a scheduled time, a PTV disruption
 *     id. Every one of them is already on a platform sign.
 *
 * `tests/share.test.js` enforces this. Adding a field means adding it there.
 *
 * ── WHY A TOKEN AND NOT A QUERY STRING ──────────────────────────────────────
 *
 * `?v=1&c=melbourne&p=board&s=1034&d=1162` is 41 characters of machinery that
 * a person has to paste into a message. Nobody does that twice. The same
 * facts are a handful of small integers, so they are packed as LEB128 varints,
 * checksummed and base64url'd, and served at `/s/<token>`: a board link comes
 * out at seven characters and the whole URL fits in a sentence.
 *
 * NO SERVER STATE. The token IS the descriptor, compressed — not a key into a
 * table of who shared what. A shortener would put a record of every link
 * anyone ever sent on a server that today holds nothing about anybody, which
 * for this app is the wrong trade at any length. Links therefore never expire
 * and there is nothing to clean up.
 *
 * WIRE FORMAT, version 1:
 *
 *   header   varint   VERSION << 4 | page index
 *   city     varint   index into CITY_WIRE — APPEND ONLY, never reorder
 *   fields   varint   one per entry in PAGES[page].fields, in order,
 *                     trailing zeros trimmed
 *   text     varint   byte length, then UTF-8 — only where PAGES[page].text
 *   check    byte     FNV-1a of everything above, low 8 bits
 *
 * The version is INSIDE the token and is read before anything else, so a
 * version this build does not know is refused rather than misread as its own.
 * Old versions keep being parsed for as long as `PARSERS` lists them.
 *
 * Nothing here throws. It is on the boot path, and a malformed link must open
 * the app, not break it.
 */

// ── The wire format's own vocabulary ─────────────────────────────────────────
//
// These three lists ARE the format. Appending to any of them is safe; editing
// or reordering an existing entry silently re-points every link ever sent, so
// do not — bump `SHARE_VERSION` instead and add a parser.

/** This build's scheme. 1..7, because it shares a byte with the page index. */
export const SHARE_VERSION = 1;

/**
 * City ids, in wire order. APPEND ONLY.
 *
 * Deliberately NOT `CITY_IDS` from city.js: that list is `Object.keys` over a
 * map somebody will one day alphabetise, and the day they do, every Sydney
 * link in every message anybody has ever sent becomes a Melbourne link. The
 * suite asserts that every city the app knows appears here, so adding one
 * without registering it fails rather than ships.
 */
export const CITY_WIRE = ['melbourne', 'sydney', 'brisbane', 'adelaide', 'canberra', 'perth'];

/**
 * The screens a link can point at, and what each one carries. APPEND ONLY.
 *
 * `fields` are non-negative integers. `text` is the single optional string
 * field — only the train has one, because a PTV run reference is not a number
 * on every line and packing it as one loses V/Line entirely.
 *
 * `route` is the app's route path for the page, which is what restore pushes.
 */
const PAGES = [
  { key: 'board', fields: ['stop', 'dest'], required: ['stop'], route: '/tabs/departures' },
  { key: 'line', fields: ['route'], required: ['route'], route: '/tabs/line' },
  {
    key: 'train',
    fields: ['route', 'stop', 'at'],
    required: ['route', 'stop', 'at', 'run'],
    text: 'run',
    route: '/tabs/train',
  },
  // A trip opens the BOARD at `from` with `to` as its destination: the board
  // plans the change now that the Trip tab has gone. The kind stays, because
  // links already sent carry it.
  { key: 'trip', fields: ['from', 'to'], required: ['from', 'to'], route: '/tabs/departures' },
  { key: 'options', fields: ['from', 'to'], required: ['from', 'to'], route: '/tabs/options' },
  { key: 'alert', fields: ['alert'], required: ['alert'], route: '/tabs/disruption' },
  // THE ONLY KIND WITH NOTHING REQUIRED, and the only one that carries no
  // moment. A map link is a PLACE, not a viewport: the station if one was
  // selected, the city if not, and never the pan and the zoom. A link that
  // restored somebody else's pan would be a link that ages badly — the reader
  // it lands on has a different screen shape, a different idea of where they
  // are and no reason to want the sender's scroll position — where a station is
  // still the same station next year. With nothing selected the city alone is
  // worth sending, so the stop field is optional and the token is four
  // characters.
  { key: 'map', fields: ['stop'], required: [], route: '/tabs/map' },
];

/** Page key → its spec. */
const PAGE_BY_KEY = new Map(PAGES.map((p, i) => [p.key, { ...p, index: i }]));

/** Every page a link can name. */
export const SHARE_PAGES = PAGES.map((p) => p.key);

/** Where the app's route for a page lives, for restore. */
export function routeForPage(page) {
  return PAGE_BY_KEY.get(String(page))?.route ?? null;
}

/**
 * Minutes since 2024-01-01T00:00:00Z.
 *
 * A scheduled departure to the minute, in three varint bytes rather than the
 * eight an epoch millisecond costs. Seconds are noise on a timetable and the
 * extra byte buys nothing.
 */
const EPOCH_MS = Date.UTC(2024, 0, 1);
const MINUTE_MS = 60_000;

/** A run reference longer than this is not a run reference. */
const MAX_TEXT_BYTES = 40;

/** Nothing this format carries is bigger than a signed 32-bit integer. */
const MAX_INT = 2 ** 31;

// ── base64url, by hand ───────────────────────────────────────────────────────
//
// Hand-rolled rather than `btoa`, for one reason: this has to REFUSE rubbish
// rather than cope with it. `atob` is lenient in ways that differ between
// engines, and a lenient decoder turns a typo into a confident wrong station.

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const INDEX = new Map([...ALPHABET].map((c, i) => [c, i]));

function toBase64Url(bytes) {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i];
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    out += ALPHABET[a >> 2];
    if (b === undefined) { out += ALPHABET[(a & 3) << 4]; break; }
    out += ALPHABET[((a & 3) << 4) | (b >> 4)];
    if (c === undefined) { out += ALPHABET[(b & 15) << 2]; break; }
    out += ALPHABET[((b & 15) << 2) | (c >> 6)];
    out += ALPHABET[c & 63];
  }
  return out;
}

/** Bytes, or null for anything that is not a well-formed base64url string. */
function fromBase64Url(text) {
  const s = String(text);
  if (!s || s.length % 4 === 1) return null;
  const bits = [];
  for (const ch of s) {
    const v = INDEX.get(ch);
    if (v === undefined) return null;
    bits.push(v);
  }
  const bytes = [];
  for (let i = 0; i < bits.length; i += 4) {
    const [a, b, c, d] = bits.slice(i, i + 4);
    if (b === undefined) return null;      // a lone sextet carries no byte
    bytes.push(((a << 2) | (b >> 4)) & 255);
    if (c === undefined) break;
    bytes.push(((b << 4) | (c >> 2)) & 255);
    if (d === undefined) break;
    bytes.push(((c << 6) | d) & 255);
  }
  return bytes;
}

// ── varints ──────────────────────────────────────────────────────────────────

function putVarint(out, value) {
  let n = value;
  while (n >= 0x80) {
    out.push((n & 0x7f) | 0x80);
    n = Math.floor(n / 128);
  }
  out.push(n);
}

/** `{ value, next }`, or null for a truncated or absurdly long varint. */
function takeVarint(bytes, at) {
  let value = 0;
  let shift = 1;
  for (let i = at; i < bytes.length && i < at + 5; i += 1) {
    const b = bytes[i];
    value += (b & 0x7f) * shift;
    if ((b & 0x80) === 0) {
      return value < MAX_INT ? { value, next: i + 1 } : null;
    }
    shift *= 128;
  }
  return null;
}

// ── the check byte ───────────────────────────────────────────────────────────
//
// FNV-1a, low eight bits. One character of token for a one-in-256 chance that
// a mangled link decodes to a plausible wrong answer instead of being refused.
// A link that loses a character in a chat app is the ordinary case, not the
// exotic one, and "Carnegie" when the sender meant "Caulfield" is the failure
// worth spending a byte to avoid.

function check(bytes) {
  let h = 0x811c9dc5;
  for (const b of bytes) {
    h ^= b;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h & 255;
}

// ── Building ─────────────────────────────────────────────────────────────────

const int = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n < MAX_INT ? Math.trunc(n) : null;
};

function utf8(text) {
  const out = [];
  for (const ch of String(text)) {
    const cp = ch.codePointAt(0);
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 63));
    else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    else {
      out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 63), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    }
  }
  return out;
}

function fromUtf8(bytes) {
  let out = '';
  let i = 0;
  while (i < bytes.length) {
    const b = bytes[i];
    let cp;
    let len;
    if (b < 0x80) { cp = b; len = 1; }
    else if ((b & 0xe0) === 0xc0) { cp = b & 31; len = 2; }
    else if ((b & 0xf0) === 0xe0) { cp = b & 15; len = 3; }
    else if ((b & 0xf8) === 0xf0) { cp = b & 7; len = 4; }
    else return null;
    if (i + len > bytes.length) return null;
    for (let k = 1; k < len; k += 1) {
      const c = bytes[i + k];
      if ((c & 0xc0) !== 0x80) return null;
      cp = (cp << 6) | (c & 63);
    }
    out += String.fromCodePoint(cp);
    i += len;
  }
  return out;
}

/**
 * A descriptor packed into a token, or null if it does not describe anything.
 *
 * Null rather than a throw, everywhere: this runs behind a button on a screen
 * whose data may not have loaded, and a share button that crashes the board is
 * worse than a share button that does not appear.
 */
export function buildShareToken(desc) {
  const spec = PAGE_BY_KEY.get(String(desc?.page ?? ''));
  if (!spec) return null;

  const cityIndex = CITY_WIRE.indexOf(String(desc?.city ?? ''));
  if (cityIndex < 0) return null;

  // Privacy, enforced here rather than trusted to callers: a descriptor that
  // carries a place kind is a bug at the call site, and it must not travel.
  if (desc.place !== undefined || desc.kind !== undefined) return null;

  const values = spec.fields.map((f) => (desc[f] === undefined || desc[f] === null ? 0 : int(desc[f])));
  if (values.some((v) => v === null)) return null;
  for (const f of spec.required) {
    if (f === spec.text) continue;
    if (!values[spec.fields.indexOf(f)]) return null;   // a required field of 0 is not a value
  }

  let text = null;
  if (spec.text) {
    text = utf8(desc[spec.text] ?? '');
    if (!text.length || text.length > MAX_TEXT_BYTES) return null;
  }

  const bytes = [];
  putVarint(bytes, (SHARE_VERSION << 4) | spec.index);
  putVarint(bytes, cityIndex);

  // Trailing zeros are absence, and absence does not need a byte. A plain
  // board is a city and a stop id; the destination slot costs nothing until
  // somebody sets one.
  let last = values.length;
  while (last > 0 && values[last - 1] === 0) last -= 1;
  for (let i = 0; i < last; i += 1) putVarint(bytes, values[i]);

  if (text) {
    putVarint(bytes, text.length);
    bytes.push(...text);
  }

  bytes.push(check(bytes));
  return toBase64Url(bytes);
}

// ── Parsing ──────────────────────────────────────────────────────────────────

/**
 * A token read back into a descriptor, or null.
 *
 * Null covers every way a link can be wrong and they are all the same answer
 * to the app: a corrupt token, a token from a newer scheme, a page this build
 * does not have, a city that does not exist, a required field left at zero.
 * The caller opens the app as usual. Guessing at a near miss would put
 * somebody on a board they were never sent.
 */
export function parseShareToken(token) {
  const bytes = fromBase64Url(token ?? '');
  if (!bytes || bytes.length < 3) return null;

  const body = bytes.slice(0, -1);
  if (bytes[bytes.length - 1] !== check(body)) return null;

  const header = takeVarint(body, 0);
  if (!header) return null;

  const version = header.value >> 4;
  const parse = PARSERS[version];
  // A link from a scheme this build has never heard of. It is not readable and
  // it is not guessable, and the version marker is the whole reason we can say
  // that rather than decode somebody else's bytes as our own.
  if (!parse) return null;

  return parse(body, header);
}

/** Version 1's body reader. Registered in `PARSERS`, and never edited again. */
function parseV1(body, header) {
  const spec = PAGES[header.value & 15];
  if (!spec) return null;

  const cityAt = takeVarint(body, header.next);
  if (!cityAt) return null;
  const city = CITY_WIRE[cityAt.value];
  if (!city) return null;

  const out = { city, page: spec.key };
  let at = cityAt.next;

  for (const field of spec.fields) {
    if (at >= body.length) { out[field] = 0; continue; }   // trimmed trailing zero
    const read = takeVarint(body, at);
    if (!read) return null;
    out[field] = read.value;
    at = read.next;
  }

  if (spec.text) {
    const len = takeVarint(body, at);
    if (!len || len.value === 0 || len.value > MAX_TEXT_BYTES) return null;
    if (at + len.value > body.length) return null;
    const text = fromUtf8(body.slice(len.next, len.next + len.value));
    if (text === null) return null;
    out[spec.text] = text;
    at = len.next + len.value;
  }

  // Bytes left over mean this is not the token it claims to be.
  if (at !== body.length) return null;

  for (const field of spec.required) {
    if (field === spec.text) continue;
    if (!out[field]) return null;
  }

  return out;
}

/**
 * Version → reader.
 *
 * ADD, never replace. A link sent in 2026 is still a link in 2028, and the
 * only thing that keeps it readable is the old reader still being here.
 */
const PARSERS = { 1: parseV1 };

// ── Times ────────────────────────────────────────────────────────────────────

/** An epoch millisecond as the minutes this format stores, or 0. */
export function packTime(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n)) return 0;
  const minutes = Math.round((n - EPOCH_MS) / MINUTE_MS);
  return minutes > 0 && minutes < MAX_INT ? minutes : 0;
}

/** And back, as an epoch millisecond, or null. */
export function unpackTime(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return null;
  return EPOCH_MS + n * MINUTE_MS;
}

// ── URLs ─────────────────────────────────────────────────────────────────────

/** The path a share link lives at. `server.js` serves the app here. */
export const SHARE_PATH = '/s/';

/**
 * The query parameter the same token can arrive as.
 *
 * `/demo?s=<token>` is how the demo honours a link, and it is the tidy form
 * for anywhere a path is not ours to choose. The path is what gets SHARED,
 * because it is the shorter of the two and it is the one people see.
 */
export const SHARE_PARAM = 's';

/** A descriptor as a link somebody can paste, or null. */
export function shareUrl(desc, origin) {
  const token = buildShareToken(desc);
  if (!token) return null;
  const base = String(origin ?? '').replace(/\/+$/, '');
  return `${base}${SHARE_PATH}${token}`;
}

/**
 * The token in a URL, from either form, or null.
 *
 * Tolerant about the URL and strict about the token: `parseShareToken` is what
 * decides whether the thing is real, and it is the only thing that does.
 */
export function shareTokenFromUrl(href) {
  try {
    const url = new URL(String(href), 'https://x.invalid');
    const fromQuery = url.searchParams.get(SHARE_PARAM);
    if (fromQuery) return fromQuery;
    const m = url.pathname.match(/\/s\/([^/]+)\/?$/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/** A URL parsed straight through to a descriptor, or null. */
export function parseShareUrl(href) {
  return parseShareToken(shareTokenFromUrl(href));
}

// ── What the message says ────────────────────────────────────────────────────

/**
 * One line, in the app's voice, carrying the fact that made somebody share it.
 *
 * A SHORT LINK SAYS NOTHING. `/s/EAHPCA` is six characters of opacity, so the
 * sentence in front of it has to do the work the old query string did: the
 * recipient should know what they have been sent before they tap, and the
 * sender should not have to type a word.
 *
 * TWO RULES, and they are the reason the wording is as flat as it is.
 *
 *   1. THE TEXT IS A SNAPSHOT AND THE LINK IS LIVE. The message is read
 *      minutes later against a board that has moved on, so nothing is worded
 *      as a promise. "Next train 9 min" is a note of what the board said;
 *      "your train is in 9 minutes" is a claim that will be wrong by the time
 *      it is read. No second person, no countdown that reads as still running.
 *
 *   2. IT IS BUILT FROM WHAT IS ALREADY ON SCREEN. Not one network call goes
 *      into writing a message. A fact that is not to hand is a clause that
 *      gets dropped, which is why every part of every sentence below is
 *      optional and the sentence still reads without it.
 *
 * LABELS ARE PASSED IN. This module holds no station names and no line names:
 * they are per city, they come out of the network bundle, and importing that
 * here would put the whole bundle behind every test of a token.
 *
 * PRIVACY, again. The labels a caller supplies are PUBLIC names off a platform
 * sign, and `publicName` below refuses the two words that are not: a screen
 * that hands this "Home" gets a sentence with the clause dropped rather than a
 * message telling a stranger which station the sender lives at.
 */

/** Words for the small counts this app ever says out loud. */
const COUNTS = ['no', 'one', 'two', 'three', 'four', 'five'];

/**
 * A label, unless it is a place kind.
 *
 * The one hard filter on the way out. Every screen is supposed to resolve a
 * place to its station before it gets here, and this is what makes a screen
 * that forgets produce a vaguer sentence rather than a privacy leak.
 */
function publicName(value) {
  const name = String(value ?? '').trim();
  if (!name) return '';
  return /^(home|work)$/i.test(name) ? '' : name;
}

/** `9 min`, or `now` for a train at the platform. Never a live countdown. */
function mins(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return '';
  return n < 1 ? 'now' : `${Math.round(n)} min`;
}

/** Only the clauses that have something behind them, joined as one sentence. */
const sentence = (parts) => `${parts.filter(Boolean).join(', ')}.`;

export function shareText(desc, labels = {}) {
  const page = String(desc?.page ?? '');
  const from = publicName(labels.from);
  const to = publicName(labels.to);
  const line = publicName(labels.line);

  if (page === 'board') {
    const head = to && from ? `${from} to ${to}` : from ? `${from} departures` : 'A departure board';
    const next = mins(labels.nextMinutes);
    if (!next) return `${head}. No trains on the board.`;
    // The direction is only said on a board with no destination. With one, the
    // destination already says which way the train is going and "next citybound
    // train to State Library" is the same fact twice.
    const dir = !to ? publicName(labels.direction) : '';
    const arrives = publicName(labels.arrives);
    return `${head}. ${sentence([
      `Next ${dir ? `${dir} ` : ''}train ${next}`,
      arrives ? `arrives ${arrives}` : '',
    ])}`;
  }

  if (page === 'train') {
    const when = publicName(labels.when);
    const head = [when, line, 'train'].filter(Boolean).join(' ');
    const where = from ? ` from ${from}` : '';
    const late = Number(labels.delayMinutes);
    // A CANCELLED TRAIN IS NOT A LATE ONE. The card carries the cancellation
    // as a block of its own and a message that reported its last estimate
    // instead would be the one sentence somebody acts on being wrong about
    // whether there is a train at all.
    const how = labels.cancelled ? 'cancelled'
      : !Number.isFinite(late) ? '' : late >= 1 ? `${Math.round(late)} min late` : 'on time';
    // A SPECIAL IS THE REASON SOMEBODY SENDS A TRAIN. A service that is not
    // running its line's usual path is the one fact on this card that cannot
    // be looked up on a timetable, and it is a clause rather than the screen's
    // own two sentences because a message is one line. The screen decides
    // whether there is one; this only says it.
    return sentence([`The ${head}${where}`, how, labels.special ? 'not on its usual path' : '']);
  }

  if (page === 'line') {
    if (!line) return 'A train line.';
    const late = Number(labels.delayMinutes);
    // A named state beats a number: "suspended" is what the sign says, and a
    // suspended line with no trains on it has no delay to quote.
    const state = publicName(labels.state);
    if (state) return sentence([`${line} line`, state]);
    if (Number.isFinite(late) && late >= 1) return sentence([`${line} line`, `delays of ${Math.round(late)} min`]);
    return sentence([`${line} line`, 'running to timetable']);
  }

  if (page === 'trip') {
    const leaves = publicName(labels.leaves);
    const arrives = publicName(labels.arrives);
    const changes = Number(labels.changes);
    const at = Array.isArray(labels.changeAt) ? labels.changeAt.map(publicName).filter(Boolean) : [];
    let change = '';
    if (Number.isFinite(changes)) {
      const word = COUNTS[changes] ?? String(changes);
      if (changes === 0) change = 'no changes';
      else change = `${word} change${changes === 1 ? '' : 's'}${at.length ? ` at ${at.join(' and ')}` : ''}`;
    }
    return sentence([
      from && to ? `${from} to ${to}` : from || to || 'A trip',
      leaves ? `leaves ${leaves}` : '',
      arrives ? `arrives ${arrives}` : '',
      change,
      // Same clause as the train's, in the place a trip can carry one: a leg
      // riding a service that is not on its line's usual path.
      labels.special ? 'on a special service' : '',
    ]);
  }

  if (page === 'options') {
    // `because` is the disruption in the sender's own words, which the options
    // screen already has on it: "the Cranbourne line down".
    const because = publicName(labels.because);
    const head = to
      ? `Getting to ${to}${because ? ` with ${because}` : ''}`
      : 'Another way around';
    const off = publicName(labels.off);
    const walk = mins(labels.walkMinutes);
    if (!off) return `${head}.`;
    return `${head}. ${sentence([`Off at ${off}`, walk && walk !== 'now' ? `then ${walk} on foot` : ''])}`;
  }

  if (page === 'map') {
    // The city is the map's own name for itself, handed in the way every other
    // label is. "Richmond on the Melbourne network map." and, with nothing
    // selected, "The Melbourne network map."
    const place = publicName(labels.city);
    const where = place ? `${place} network map` : 'network map';
    const station = publicName(labels.station);
    return station ? `${station} on the ${where}.` : `The ${where}.`;
  }

  if (page === 'alert') {
    // The notice's own headline. It is written by the operator, it is already
    // one line, and rewording an operator's disruption notice is how a share
    // ends up saying something the operator did not.
    const headline = publicName(labels.headline);
    return headline ? `${headline.replace(/[.\s]+$/, '')}.` : 'A disruption.';
  }

  return 'Late Again.';
}

/**
 * The whole payload a share sheet wants.
 *
 * THE LINK GOES LAST AND ON ITS OWN LINE. Messaging apps detect a URL at the
 * end of a message and render it as a link with a preview; one buried inside a
 * sentence gets swallowed by the punctuation next to it often enough to matter.
 * Nothing is appended after it — no app name, no tagline. The link's own
 * preview can carry the brand when there is one, and a signature on somebody
 * else's message is an advertisement they did not agree to send.
 */
export function sharePayload(desc, { origin, labels } = {}) {
  const url = shareUrl(desc, origin);
  if (!url) return null;
  const text = shareText(desc, labels);
  return { title: text, text, url, message: `${text}\n${url}` };
}
